import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
	CreateProductDTO,
	PriceCalculationResponseDTO,
	ProductFiltersDTO,
	ProductListResponseDTO,
	ProductResponseDTO,
	PublicProductListResponseDTO,
	PublicProductResponseDTO,
	UpdateProductDTO,
} from '@libs/dtos/product/product.dto';
import { Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { fetchAllPages } from '../../utils/fetch-all-pages';

@Injectable({
	providedIn: 'root',
})
export class ProductService {
	private readonly baseUrl = `${environment.pathBackend}/products`;

	/** How long a platform's public catalog is reused before asking the API again */
	private static readonly PUBLIC_CACHE_TTL_MS = 5 * 60 * 1000;

	// Public catalog per platform, with the time it was fetched
	private cachedPublicProductsByPlatform = new Map<string, { at: number; data: PublicProductListResponseDTO }>();

	constructor(private http: HttpClient) {}

	getProducts(params?: Partial<ProductFiltersDTO>): Observable<ProductListResponseDTO> {
		return this.http.get<ProductListResponseDTO>(this.baseUrl, { params });
	}

	getProductById(id: string): Observable<ProductResponseDTO> {
		return this.http.get<ProductResponseDTO>(`${this.baseUrl}/${id}`);
	}

	createProduct(data: CreateProductDTO): Observable<ProductResponseDTO> {
		return this.http.post<ProductResponseDTO>(this.baseUrl, data).pipe(tap(() => this.clearCache()));
	}

	updateProduct(id: string, data: UpdateProductDTO): Observable<ProductResponseDTO> {
		return this.http.put<ProductResponseDTO>(`${this.baseUrl}/${id}`, data).pipe(tap(() => this.clearCache()));
	}

	deleteProduct(id: string): Observable<void> {
		return this.http.delete<void>(`${this.baseUrl}/${id}`).pipe(tap(() => this.clearCache()));
	}

	/** Deletes several products in one request */
	bulkDeleteProducts(ids: number[]): Observable<{ affected: number }> {
		return this.http
			.post<{ affected: number }>(`${this.baseUrl}/bulk-delete`, { ids })
			.pipe(tap(() => this.clearCache()));
	}

	/** Sets the display order of a platform's products (order of `ids`) */
	reorderProducts(platformId: string, ids: string[]): Observable<void> {
		return this.http.put<void>(`${this.baseUrl}/reorder`, { platformId, ids }).pipe(tap(() => this.clearCache()));
	}

	setActiveProvider(productId: string, productProviderId: string): Observable<ProductResponseDTO> {
		return this.http
			.patch<ProductResponseDTO>(`${this.baseUrl}/${productId}/active-provider/${productProviderId}`, {})
			.pipe(tap(() => this.clearCache()));
	}

	// Frontend-specific methods
	getProductsByPlatform(platformId: string, params?: Partial<ProductFiltersDTO>): Observable<ProductListResponseDTO> {
		// Whole platform list, every page (the API paginates at 10 by default)
		return fetchAllPages((paging) =>
			this.http.get<ProductListResponseDTO>(this.baseUrl, { params: { ...params, ...paging, platformId } }),
		);
	}

	// Products by type (Likes, Views, etc.)
	getProductsByType(productType: string, params?: Partial<ProductFiltersDTO>): Observable<ProductListResponseDTO> {
		const queryParams = { ...params, name: productType };
		return this.http.get<ProductListResponseDTO>(this.baseUrl, { params: queryParams });
	}

	// Price for a custom quantity
	calculatePrice(productId: number, quantity: number): Observable<PriceCalculationResponseDTO> {
		return this.http
			.post<PriceCalculationResponseDTO>(`${this.baseUrl}/calculate-price`, {
				productId: productId.toString(),
				quantity,
			})
			.pipe(
				catchError(() => {
					return throwError(() => new Error('No se pudo calcular el precio'));
				}),
			);
	}

	// ==================== PUBLIC METHODS (no authentication) ====================

	getPublicProducts(params?: Partial<ProductFiltersDTO>): Observable<PublicProductListResponseDTO> {
		return this.http.get<PublicProductListResponseDTO>(`${this.baseUrl}/public`, { params });
	}

	getPublicProductById(id: string): Observable<PublicProductResponseDTO> {
		return this.http.get<PublicProductResponseDTO>(`${this.baseUrl}/public/${id}`);
	}

	getPublicProductsByPlatform(
		platformId: string,
		params?: Partial<ProductFiltersDTO>,
	): Observable<PublicProductListResponseDTO> {
		// Return cached data while it is fresh
		const cached = this.freshPublicCache(platformId);
		if (cached) {
			return of(cached);
		}

		return fetchAllPages((paging) =>
			this.http.get<PublicProductListResponseDTO>(`${this.baseUrl}/public`, {
				params: { ...params, ...paging, platformId },
			}),
		).pipe(
			tap((response) => {
				this.cachedPublicProductsByPlatform.set(platformId, { at: Date.now(), data: response });
			}),
		);
	}

	/**
	 * Preload public products for a specific platform
	 */
	preloadPublicProductsByPlatform(platformId: string): void {
		if (!this.freshPublicCache(platformId)) {
			this.getPublicProductsByPlatform(platformId).subscribe();
		}
	}

	/** A product changed (or the cache is stale): the next public read goes to the API */
	clearCache(): void {
		this.cachedPublicProductsByPlatform.clear();
	}

	private freshPublicCache(platformId: string): PublicProductListResponseDTO | null {
		const entry = this.cachedPublicProductsByPlatform.get(platformId);
		if (!entry) return null;
		if (Date.now() - entry.at > ProductService.PUBLIC_CACHE_TTL_MS) {
			this.cachedPublicProductsByPlatform.delete(platformId);
			return null;
		}
		return entry.data;
	}
}
